"use client";

import { useQueryState } from "nuqs";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Scrollbar, ScrollbarSkeleton } from "@/components/ui/Scrollbar";
import { Skeleton } from "@/components/ui/Skeleton";
import { ARTICLES_FILTER_TAG } from "@/features/articles/ArticleListingPage/config";
import { useTranslations } from "@/i18n/client";

export interface TagFilterProps {
	tags: {
		name: string;
		count: number;
	}[];
}

export const TagFilter = ({ tags }: TagFilterProps) => {
	const t = useTranslations();
	const [selectedTag, setSelectedTag] = useQueryState(ARTICLES_FILTER_TAG);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	const toggleTag = (tagName: string) => {
		if (selectedTag === tagName) {
			setSelectedTag(null);
		} else {
			setSelectedTag(tagName);
		}
	};

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const updateScrollInfo = () => {
			const containerWidth = container.clientWidth;
			const scrollWidth = container.scrollWidth;
			const scrollLeft = container.scrollLeft;

			if (scrollWidth <= containerWidth) {
				setTotalPages(1);
				setCurrentPage(1);
				return;
			}
			const pages = Math.ceil(scrollWidth / containerWidth);
			setTotalPages(pages);

			const scrollableWidth = scrollWidth - containerWidth;
			const scrollProgress = scrollLeft / scrollableWidth;
			const page = Math.min(pages, Math.max(1, Math.ceil(scrollProgress * pages) || 1));
			setCurrentPage(page);
		};

		updateScrollInfo();

		container.addEventListener("scroll", updateScrollInfo);

		const resizeObserver = new ResizeObserver(updateScrollInfo);
		resizeObserver.observe(container);

		return () => {
			container.removeEventListener("scroll", updateScrollInfo);
			resizeObserver.disconnect();
		};
	}, []);

	const handlePrev = () => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const scrollAmount = container.clientWidth;
		container.scrollBy({
			left: -scrollAmount,
			behavior: "smooth",
		});
	};

	const handleNext = () => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const scrollAmount = container.clientWidth;
		container.scrollBy({
			left: scrollAmount,
			behavior: "smooth",
		});
	};

	return (
		<div className="flex flex-col gap-grid-section-vertical-md">
			<div ref={scrollContainerRef} className="flex gap-2 overflow-x-auto scrollbar-hide">
				{tags.map((tag) => (
					<Button
						variant={selectedTag === tag.name ? "solid" : "outline"}
						size="large"
						key={tag.name}
						onClick={() => toggleTag(tag.name)}
						toolTip={tag.count}
						className="shrink-0 rounded-component-specific-button"
					>
						{t(tag.name)}
					</Button>
				))}
			</div>
			{totalPages > 1 && (
				<Scrollbar
					current={currentPage}
					total={totalPages}
					onPrev={handlePrev}
					onNext={handleNext}
				/>
			)}
		</div>
	);
};

export const TagFilterSkeleton = ({ size = 4 }: { size?: number }) => {
	return (
		<div className="flex flex-col gap-grid-section-vertical-md">
			<div className="flex gap-2 overflow-x-auto scrollbar-hide">
				{Array.from({ length: size }).map((_, i) => (
					<Skeleton fitContent key={i}>
						<Button
							variant={"solid"}
							size="large"
							className="shrink-0 rounded-component-specific-button"
						>
							Loremipsum
						</Button>
					</Skeleton>
				))}
			</div>
			<ScrollbarSkeleton />
		</div>
	);
};
